import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { Check, Pencil, X, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import {
  GET_USER_PROFILE,
  UPDATE_USER_PROFILE,
  SYNC_USER_PROFILE_TO_ALL_CARDS,
} from "../graphql/user";
import { GradientHeader } from "../components/GradientHeader";
import { SectionCard } from "../components/SectionCard";
import { PageContainer } from "../components/PageContainer";
import { enableBackButton, hapticFeedback, getUserInfo } from "../lib/telegram";

type ProfileForm = {
  name: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  dob: string;
  ssn_last4: string;
};

const EMPTY_FORM: ProfileForm = {
  name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  zip_code: "",
  country: "US",
  dob: "",
  ssn_last4: "",
};

export const Profile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const telegramUser = getUserInfo();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ProfileForm>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery(GET_USER_PROFILE, {
    variables: { userId: user?.id },
    skip: !user?.id,
    fetchPolicy: "cache-and-network",
  });

  const [updateProfile, { loading: saving }] = useMutation(UPDATE_USER_PROFILE);
  const [syncToCards, { loading: syncing }] = useMutation(
    SYNC_USER_PROFILE_TO_ALL_CARDS
  );

  useEffect(() => enableBackButton(() => navigate("/settings")), [navigate]);

  // Hydrate form whenever the underlying data changes (entering edit mode).
  useEffect(() => {
    const u = data?.users_by_pk;
    if (!u) return;
    setForm({
      name: u.name || "",
      address_line1: u.address_line1 || "",
      address_line2: u.address_line2 || "",
      city: u.city || "",
      state: u.state || "",
      zip_code: u.zip_code || "",
      country: u.country || "US",
      dob: u.dob || "",
      ssn_last4: u.ssn_last4 || "",
    });
  }, [data]);

  const profile = data?.users_by_pk;
  const physicalCardCount: number =
    profile?.physical_cards_aggregate?.aggregate?.count ?? 0;

  const initial = useMemo(
    () =>
      (profile?.name || telegramUser?.first_name || "U")
        .charAt(0)
        .toUpperCase(),
    [profile?.name, telegramUser?.first_name]
  );

  const handleCancel = () => {
    if (!profile) return;
    setForm({
      name: profile.name || "",
      address_line1: profile.address_line1 || "",
      address_line2: profile.address_line2 || "",
      city: profile.city || "",
      state: profile.state || "",
      zip_code: profile.zip_code || "",
      country: profile.country || "US",
      dob: profile.dob || "",
      ssn_last4: profile.ssn_last4 || "",
    });
    setError(null);
    setEditing(false);
  };

  const handleSave = async () => {
    if (!user?.id) return;
    setError(null);
    setSuccess(null);

    const trimmed = {
      name: form.name.trim(),
      address_line1: form.address_line1.trim() || null,
      address_line2: form.address_line2.trim() || null,
      city: form.city.trim() || null,
      state: form.state.trim().toUpperCase() || null,
      zip_code: form.zip_code.trim() || null,
      country: form.country.trim().toUpperCase() || null,
      dob: form.dob || null,
      ssn_last4: form.ssn_last4.replace(/\D/g, "").slice(-4) || null,
    };

    if (!trimmed.name) {
      setError("Name is required.");
      return;
    }
    if (trimmed.state && trimmed.state.length !== 2) {
      setError("State must be a 2-letter abbreviation (e.g. NY).");
      return;
    }
    if (trimmed.ssn_last4 && !/^\d{4}$/.test(trimmed.ssn_last4)) {
      setError("SSN last 4 must be exactly 4 digits.");
      return;
    }

    try {
      await updateProfile({
        variables: { userId: user.id, ...trimmed },
      });
      hapticFeedback?.success?.();

      // If the user has any physical cards, push the updated profile to EFS.
      if (physicalCardCount > 0) {
        try {
          const r = await syncToCards({
            variables: { input: { userId: user.id } },
          });
          const out = r.data?.syncUserProfileToAllCards;
          if (out?.success) {
            setSuccess(
              out.total > 0
                ? `Profile saved and synced to ${out.synced}/${out.total} card(s).`
                : "Profile saved."
            );
          } else {
            setSuccess(
              "Profile saved. Card sync had issues — an admin will retry."
            );
          }
        } catch {
          setSuccess(
            "Profile saved. Card sync failed — an admin will retry."
          );
        }
      } else {
        setSuccess("Profile saved.");
      }

      setEditing(false);
      await refetch();
    } catch (e: any) {
      hapticFeedback?.error?.();
      setError(e?.message || "Failed to save profile");
    }
  };

  if (loading && !profile) {
    return (
      <PageContainer>
        <GradientHeader size="sm">
          <p className="font-display text-xl font-semibold text-ink tracking-[-0.02em]">Profile</p>
        </GradientHeader>
        <div className="px-4 mt-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-accent" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <GradientHeader size="md">
        <div className="flex items-center gap-3.5">
          <div className="w-14 h-14 rounded-full bg-ink flex items-center justify-center">
            <span className="text-2xl font-bold text-ground">{initial}</span>
          </div>
          <div>
            <p className="font-display text-xl font-semibold text-ink tracking-[-0.02em]">
              {profile?.name || "Profile"}
            </p>
            <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted">
              {editing ? "Editing profile" : "Driver profile"}
            </p>
          </div>
        </div>
      </GradientHeader>

      {(error || success) && (
        <div className="px-4 mt-3">
          {error && (
            <div className="bg-surface border-l-[3px] border-error rounded-md p-3 flex items-start gap-2 text-sm text-error">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="bg-surface border-l-[3px] border-success rounded-md p-3 flex items-start gap-2 text-sm text-ink">
              <Check className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{success}</span>
            </div>
          )}
        </div>
      )}

      {/* Read-only identifiers */}
      <div className="px-4 mt-3">
        <SectionCard title="Identifiers">
          <Row label="Email" value={profile?.email || "—"} />
          <Divider />
          <Row label="Phone" value={profile?.phone || "—"} />
          <Divider />
          <Row
            label="Telegram"
            value={
              telegramUser?.username
                ? `@${telegramUser.username}`
                : telegramUser?.first_name || "Connected"
            }
          />
        </SectionCard>
      </div>

      {/* Editable driver profile */}
      <div className="px-4 mt-4">
        <SectionCard title="Driver profile">
          {editing ? (
            <div className="space-y-3">
              <Field
                label="Full name *"
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                placeholder="First Last"
              />
              <Field
                label="Address line 1"
                value={form.address_line1}
                onChange={(v) => setForm({ ...form, address_line1: v })}
                placeholder="123 Main St"
              />
              <Field
                label="Address line 2"
                value={form.address_line2}
                onChange={(v) => setForm({ ...form, address_line2: v })}
                placeholder="Apt, suite, etc."
              />
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="City"
                  value={form.city}
                  onChange={(v) => setForm({ ...form, city: v })}
                />
                <Field
                  label="State"
                  value={form.state}
                  onChange={(v) =>
                    setForm({ ...form, state: v.toUpperCase().slice(0, 2) })
                  }
                  placeholder="NY"
                  maxLength={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Zip code"
                  value={form.zip_code}
                  onChange={(v) => setForm({ ...form, zip_code: v })}
                  placeholder="10001"
                />
                <Field
                  label="Country"
                  value={form.country}
                  onChange={(v) =>
                    setForm({ ...form, country: v.toUpperCase() })
                  }
                  placeholder="US"
                  maxLength={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Date of birth"
                  type="date"
                  value={form.dob}
                  onChange={(v) => setForm({ ...form, dob: v })}
                />
                <Field
                  label="SSN (last 4)"
                  value={form.ssn_last4}
                  onChange={(v) =>
                    setForm({
                      ...form,
                      ssn_last4: v.replace(/\D/g, "").slice(0, 4),
                    })
                  }
                  placeholder="1234"
                  maxLength={4}
                  inputMode="numeric"
                />
              </div>
            </div>
          ) : (
            <>
              <Row label="Name" value={profile?.name || "—"} />
              <Divider />
              <Row label="Address line 1" value={profile?.address_line1 || "—"} />
              <Divider />
              <Row label="Address line 2" value={profile?.address_line2 || "—"} />
              <Divider />
              <Row label="City" value={profile?.city || "—"} />
              <Divider />
              <Row label="State" value={profile?.state || "—"} />
              <Divider />
              <Row label="Zip code" value={profile?.zip_code || "—"} />
              <Divider />
              <Row label="Country" value={profile?.country || "—"} />
              <Divider />
              <Row label="Date of birth" value={profile?.dob || "—"} />
              <Divider />
              <Row
                label="SSN (last 4)"
                value={profile?.ssn_last4 ? `•••• ${profile.ssn_last4}` : "—"}
              />
            </>
          )}
        </SectionCard>
      </div>

      {/* Actions */}
      <div className="px-4 mt-4 mb-6 flex gap-2">
        {editing ? (
          <>
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving || syncing}
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-ground border border-hairline text-muted font-semibold rounded-xl py-3 text-sm disabled:opacity-50"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || syncing}
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-ink text-ground font-semibold rounded-xl py-3 text-sm disabled:opacity-50"
            >
              {saving || syncing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {syncing ? "Syncing cards…" : saving ? "Saving…" : "Save profile"}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => {
              setSuccess(null);
              setError(null);
              setEditing(true);
            }}
            className="w-full inline-flex items-center justify-center gap-1.5 bg-ink text-ground font-semibold rounded-xl py-3 text-sm"
          >
            <Pencil className="h-4 w-4" />
            Edit profile
          </button>
        )}
      </div>

      {physicalCardCount > 0 && (
        <div className="px-4 -mt-2 mb-6">
          <p className="text-xs text-muted leading-relaxed">
            Saving updates {physicalCardCount} physical card
            {physicalCardCount === 1 ? "" : "s"} on EFS with this driver
            profile.
          </p>
        </div>
      )}
    </PageContainer>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between items-center py-1.5">
    <span className="text-muted text-sm">{label}</span>
    <span className="text-sm font-medium text-ink text-right max-w-[60%] break-words">
      {value}
    </span>
  </div>
);

const Divider = () => <div className="h-px bg-hairline" />;

type FieldProps = {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  maxLength?: number;
  inputMode?: "numeric" | "text";
};

const Field = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  maxLength,
  inputMode,
}: FieldProps) => (
  <label className="block">
    <span className="block font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-muted mb-1">
      {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      inputMode={inputMode}
      className="w-full bg-ground border border-hairline rounded-xl px-3 py-2.5 text-sm text-ink placeholder-muted/40 focus:outline-none focus:border-accent focus:bg-surface"
    />
  </label>
);

export default Profile;
